import { KLineData } from "klinecharts";
import { klineLineStyle, lineStyleToKLine } from "./lineStyle";
import { getPriceValue, readStyle, shiftResults } from "./indicatorUtils";

export const createStandardDeviation = (settings: any) => {
  const values = settings?.values ?? {};
  const styles = settings?.styles ?? settings?.styleSettings ?? {};
  const period = (values.Period as number) ?? 20;
  const shift = (values.Shift as number) ?? 0;
  const method = (values.Method as string) ?? "Simple";
  const applyTo = (values["Apply to"] as string) ?? "Close";
  const stdS = readStyle(styles, "std", "#2196F3");

  return {
    name: "std-dev",
    shortName: `StdDev${period}`,
    calcParams: [period, shift, method, applyTo],
    series: "normal" as const,
    figures: [{ key: "std", title: `StdDev${period}: `, type: "line" }],
    styles: {
      lines: [klineLineStyle(stdS.color, 2, lineStyleToKLine(stdS.style))],
    },
    regenerateFigures: ([p]: number[]) => [
      { key: "std", title: `StdDev${p}: `, type: "line" },
    ],
    calc: (dataList: KLineData[], indicator: { calcParams?: unknown[] }) => {
      const [p, , maMethod, priceField] = (indicator.calcParams ?? [
        period,
        shift,
        method,
        applyTo,
      ]) as [number, number, string, string];
      const n = dataList.length;
      const results: Array<{ std?: number }> = new Array(n);

      let sum = 0;
      let sumSquares = 0;
      let prevEma = 0;
      const expMultiplier = 2 / (p + 1);

      for (let i = 0; i < n; i++) {
        const priceValue = getPriceValue(dataList[i]!, priceField);

        if (maMethod === "Exponential") {
          if (i === 0) prevEma = priceValue;
          else prevEma = (priceValue - prevEma) * expMultiplier + prevEma;
          if (i >= p - 1) {
            results[i] = { std: Math.abs(priceValue - prevEma) };
          } else {
            results[i] = {};
          }
          continue;
        }

        sum += priceValue;
        sumSquares += priceValue * priceValue;
        if (i >= p) {
          const out = getPriceValue(dataList[i - p]!, priceField);
          sum -= out;
          sumSquares -= out * out;
        }
        if (i >= p - 1) {
          const mean = sum / p;
          const variance = Math.max(0, sumSquares / p - mean * mean);
          results[i] = { std: Math.sqrt(variance) };
        } else {
          results[i] = {};
        }
      }

      const [, shiftParam] = (indicator.calcParams ?? []) as [number, number];
      return shiftParam !== 0 ? shiftResults(results, shiftParam) : results;
    },
  };
};
